import React, { useState } from 'react';
import '../styles/components/FilterBar.css';
import { ResourceFilterConfig } from '../types/filterTypes';

export type FilterAction = 
  | { type: 'REMOVE', key: string, value?: any }
  | { type: 'EDIT', key: string, oldValue: any, newValue: string };

interface FilterBarProps {
  filters: Record<string, any>;
  filterConfig: ResourceFilterConfig;
  onFilterChange: (action: FilterAction) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ 
  filters, 
  filterConfig,
  onFilterChange 
}) => {
  // State to track which filter is being edited and its temporary value
  const [editingFilter, setEditingFilter] = useState<{ key: string; value: any } | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  if (Object.keys(filters).length === 0) {
    return null;
  }

  // Start editing a filter
  const handleFilterClick = (key: string, value: any) => {
    setEditingFilter({ key, value });
    setEditValue(String(value));
  };

  // Handle keyboard events during editing
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editingFilter) {
      // Confirm changes
      onFilterChange({ 
        type: 'EDIT', 
        key: editingFilter.key, 
        oldValue: editingFilter.value, 
        newValue: editValue 
      });
      setEditingFilter(null);
    } else if (e.key === 'Escape') {
      // Cancel editing
      setEditingFilter(null);
    }
  };

  // Confirm changes when clicking outside
  const handleBlur = () => {
    if (editingFilter) {
      onFilterChange({ 
        type: 'EDIT', 
        key: editingFilter.key, 
        oldValue: editingFilter.value, 
        newValue: editValue 
      });
      setEditingFilter(null);
    }
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: string, value?: any) => {
    onFilterChange({ type: 'REMOVE', key, value });
  };

  // Get the display label for a filter key from config
  const getFilterLabel = (key: string): string => {
    const filterDef = filterConfig.filters.find(f => f.key === key);
    return filterDef?.label || key;
  };

  // Function to create filter tags
  const renderFilterTags = () => {
    return Object.entries(filters).map(([key, value]) => {
      const displayLabel = getFilterLabel(key);
      
      // Handle array values
      if (Array.isArray(value)) {
        return value.map((item, index) => {
          const isEditing = editingFilter && 
                           editingFilter.key === key && 
                           editingFilter.value === item;
          
          return (
            <div key={`${key}-${index}`} className={`filter-tag ${isEditing ? 'editing' : ''}`}>
              {isEditing ? (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  autoFocus
                  className="filter-edit-input"
                />
              ) : (
                <span 
                  className="filter-tag-content"
                  onClick={() => handleFilterClick(key, item)}
                >
                  {displayLabel}: {item}
                </span>
              )}
              <button
                className="filter-remove-button"
                onClick={() => handleRemoveFilter(key, item)}
                aria-label={`Remove ${displayLabel}: ${item} filter`}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          );
        });
      } else {
        // Handle single values
        const isEditing = editingFilter && 
                         editingFilter.key === key && 
                         editingFilter.value === value;
        
        return (
          <div key={key} className={`filter-tag ${isEditing ? 'editing' : ''}`}>
            {isEditing ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                autoFocus
                className="filter-edit-input"
              />
            ) : (
              <span 
                className="filter-tag-content"
                onClick={() => handleFilterClick(key, value)}
              >
                {displayLabel}: {value}
              </span>
            )}
            <button
              className="filter-remove-button"
              onClick={() => handleRemoveFilter(key)}
              aria-label={`Remove ${displayLabel} filter`}
            >
              <span className="material-icons">close</span>
            </button>
          </div>
        );
      }
    });
  };
  
  return (
    <div className="filter-bar">
      {renderFilterTags()}
    </div>
  );
};

export default FilterBar; 